import React from 'react'
import { AppBar, IconButton, Typography, Menu, Tooltip, MenuItem, useMediaQuery, Theme, Box, Link } from '@mui/material'
import { Menu as MenuIcon } from '@mui/icons-material'
import { Logo, LogoSecondary } from 'src/common/assets'
import { useAuth, useRoles } from 'src/common/context'
import { PartnerRole } from 'src/common/static'
import * as S from './styles'
import { To, useMatch, useResolvedPath } from 'react-router-dom'
import useNavbar from './Navbar.state'

interface NavItem {
  link: string
  path: string
  requiredRole?: PartnerRole
}

const pagesLoggedIn: NavItem[] = [
  {
    link: 'Dashboard',
    path: '/'
  },
  {
    link: 'Orders',
    path: '/orders'
  },
  {
    link: 'Mint',
    path: '/mint',
    requiredRole: PartnerRole.OPS
  },
  {
    link: 'Burn',
    path: '/burn',
    requiredRole: PartnerRole.OPS
  },
  {
    link: 'Wallets',
    path: '/wallets'
  },
  {
    link: 'Earnings',
    path: '/earnings',
    requiredRole: PartnerRole.FINANCE
  }
]
const pagesLoggedOut = [
  {
    link: 'QCAD',
    path: 'https://www.stablecorp.ca/'
  },
  {
    link: 'Learn',
    path: 'https://stablecorpdigital.gitbook.io/sdc/'
  },
  {
    link: 'Company',
    path: 'https://www.stablecorp.ca/'
  }
]
interface SettingItem {
  item: string
  path: string
  requiredRole?: PartnerRole
}

const settings: SettingItem[] = [
  {
    item: 'Profile',
    path: '/profile'
  },
  {
    item: 'Wallets',
    path: '/wallets',
    requiredRole: PartnerRole.OPS
  }
  // {
  //   item: 'Payment',
  //   path: '/payment'
  // }
  // {
  //   item: 'Security',
  //   path: '/security'
  // }
]

const Navbar: React.FC = () => {
  const { user, logout } = useAuth()
  const { hasAnyRole } = useRoles()
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const visiblePages = pagesLoggedIn.filter((page) => !page.requiredRole || hasAnyRole(page.requiredRole))
  const visibleSettings = settings.filter((setting) => !setting.requiredRole || hasAnyRole(setting.requiredRole))

  const { anchorElNav, anchorElUser, handleOpenNavMenu, handleOpenUserMenu, handleCloseNavMenu, handleCloseUserMenu } =
    useNavbar()

  return (
    <>
      <AppBar color="inherit">
        <S.NavToolbar>
          <S.LogoContainer href="/">
            {mobile ? (
              <LogoSecondary width="100%" height="100%" />
            ) : (
              <Logo width="70%" height="70%" style={{ margin: 'auto' }} />
            )}
          </S.LogoContainer>
          <S.NavMenuContainer sx={{ display: { xs: 'flex', lg: 'none' } }}>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{ display: { xs: 'block', lg: 'none' } }}
            >
              {(user ? visiblePages : pagesLoggedOut).map(({ link, path }, i) => (
                <MenuItem key={i} component={user ? Link : 'a'} onClick={handleCloseNavMenu} href={path}>
                  <Typography>{link}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </S.NavMenuContainer>
          <S.NavLinksContainer sx={{ display: { xs: 'none', lg: 'inline-flex' } }}>
            {(user ? visiblePages : pagesLoggedOut).map((page) => (
              <CustomLink
                key={page.link}
                component={user ? undefined : 'a'}
                href={page.path}
                target={user ? undefined : '_blank'}
                rel={user ? undefined : 'noopener'}
                underline="none"
                onClick={handleCloseNavMenu}
              >
                {page.link}
              </CustomLink>
            ))}
          </S.NavLinksContainer>
          {user ? (
            <>
              {/* <ConnectWallet /> */}
              <Tooltip title="Account">
                <S.AvatarIconButton onClick={handleOpenUserMenu}>
                  <S.AvatarInitials alt="user-initials">
                    {user.given_name.charAt(0) + user.family_name.charAt(0)}
                  </S.AvatarInitials>
                </S.AvatarIconButton>
              </Tooltip>
              <Menu
                sx={{ mt: '48px' }}
                id="menu-account"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right'
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {visibleSettings.map(({ item, path }, i) => (
                  <MenuItem key={i} component={Link} onClick={handleCloseUserMenu} href={path}>
                    <Typography>{item}</Typography>
                  </MenuItem>
                ))}
                <MenuItem
                  onClick={() => {
                    logout()
                    handleCloseUserMenu()
                  }}
                >
                  Logout
                </MenuItem>
              </Menu>
            </>
          ) : (
            <S.AuthButton href="/login" size="small">
              Log In
            </S.AuthButton>
          )}
        </S.NavToolbar>
      </AppBar>
      <Box height={75} />
    </>
  )
}

// weirdly spreading props throws a TS error hence the individual prop assigment
const CustomLink: React.FC<S.NavLinkProps> = ({
  component,
  href,
  target,
  rel,
  underline,
  onClick,
  children
}: S.NavLinkProps) => {
  const resolved = useResolvedPath(href as To)
  const match = useMatch({ path: resolved.pathname, end: href === '/' })

  return (
    <S.NavLink
      component={component}
      href={href}
      target={target}
      rel={rel}
      underline={underline}
      onClick={onClick}
      active={Boolean(match)}
    >
      {children}
    </S.NavLink>
  )
}

export default Navbar
